import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { axInst } from "../../Components/Login/Service";
import { useSelector } from "react-redux";
import { IoIosArrowForward, IoIosArrowDown } from "react-icons/io";
import { IoArrowUp, IoArrowDown } from "react-icons/io5";

const fetchMainData = async (endpoint, body) => {
  const res = await axInst.post(endpoint, body);
  return Object.entries(res.data).map(([lob, val]) => ({
    lob,
    lmtd_count: val.LMTD.count,
    lmtd_sal: val.LMTD.salience,
    mtd_count: val.MTD.count,
    mtd_sal: val.MTD.salience,
    change: val.Change,
  }));
};

const NestedColumnTable = ({ endpoint, Heading, expansionEndpoint }) => {
  const [expanded, setExpanded] = useState({});
  const [detailData, setDetailData] = useState({});
  const { data, submitted } = useSelector((e) => e.filtersSalesSuccessCenter);

  const {
    data: SalienceData,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [endpoint, submitted],
    queryFn: () => fetchMainData(endpoint, data),
    cacheTime: 0,
    staleTime: 0,
  });

  const columns = [
    {
      header: "LOB",
      accessorKey: "lob",
      id: "lob",
    },
    {
      header: "LMTD",
      id: "lmtd",
      columns: [
        {
          header: "Qty",
          accessorKey: "lmtd_count",
          id: "lmtd_count",
        },
        {
          header: "Sal",
          accessorKey: "lmtd_sal",
          id: "lmtd_sal",
          cell: ({ getValue }) => `${getValue()}%`,
        },
      ],
    },
    {
      header: "MTD",
      id: "mtd",
      columns: [
        {
          header: "Qty",
          accessorKey: "mtd_count",
          id: "mtd_count",
        },
        {
          header: "Sal",
          accessorKey: "mtd_sal",
          id: "mtd_sal",
          cell: ({ getValue }) => `${getValue()}%`,
        },
      ],
    },

    {
      header: "Change",
      accessorKey: "change",
      id: "change",
      cell: ({ getValue }) => {
        const value = getValue();
        const num = parseFloat(value);

        const isPositive = !isNaN(num) && num > 0;
        const isNegative = !isNaN(num) && num < 0;

        return (
          <span
            className={`inline-flex items-center gap-1 ${
              isPositive ? "text-green-600" : isNegative ? "text-red-600" : ""
            }`}
          >
            {isPositive && <IoArrowUp />}
            {isNegative && <IoArrowDown />}
            {value}%
          </span>
        );
      },
    },
  ];

  const table = useReactTable({
    data: SalienceData ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    state: { expanded },
    onExpandedChange: setExpanded,
    getRowCanExpand: () => true,
  });

  const handleRowClick = async (row) => {
    const lob = row.original.lob;
    if (!detailData[lob]) {
      const details = await fetchDetailData(expansionEndpoint, lob);
      setDetailData((prev) => ({ ...prev, [lob]: details }));
    }
    row.toggleExpanded();
  };

  const fetchDetailData = async (expansionEndpoint, productLine) => {
    const res = await axInst.post(expansionEndpoint, {
      Product_Line: [productLine],
    });
    return Object.entries(res.data).map(([slab, val]) => ({
      slab,
      lmtd_count: val.LMTD.count,
      lmtd_sal: val.LMTD.salience,
      mtd_count: val.MTD.count,
      mtd_sal: val.MTD.salience,
      change: val.Change,
    }));
  };

  if (isFetching) return <div className="p-4">Loading...</div>;
  if (isError)
    return <div className="p-4 text-red-500">Failed to load data</div>;

  return (
    <div className=" overflow-x-auto bg-white rounded-xl shadow">
      <div className="p-2 text-base font-semibold text-[#4c4c4c]">
        {Heading}
      </div>
      <div className="max-h-[15rem] overflow-y-auto border border-gray-300">
        <table className="min-w-full border border-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border border-gray-300">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="border border-gray-300 text-center bg-[#2A5775] text-white text-sm"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => (
              <React.Fragment key={row.id}>
                <tr
                  className={`cursor-pointer hover:bg-gray-50 ${
                    rowIndex % 2 === 0 ? "bg-white" : "bg-gray-100"
                  }`}
                  onClick={() => handleRowClick(row)}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td
                      key={cell.id}
                      className={`border px-2 py-1 text-sm text-center ${
                        cellIndex === 0
                          ? "bg-[#D3DDE6] text-left font-medium"
                          : ""
                      }`}
                    >
                      {cellIndex === 0 && (
                        <span className="inline-flex items-center gap-1">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                          {row.getIsExpanded() ? (
                            <IoIosArrowDown className="inline" />
                          ) : (
                            <IoIosArrowForward className="inline" />
                          )}
                        </span>
                      )}
                      {cellIndex !== 0 &&
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                    </td>
                  ))}
                </tr>

                {row.getIsExpanded() &&
                  detailData[row.original.lob]?.map((child, idx) => (
                    <tr
                      key={idx}
                      className="text-white text-sm text-center bg-[#234B67] w-full"
                    >
                      <td className="border px-2 py-1 pl-6 bg-[#234B67]">
                        {child.slab}
                      </td>
                      <td className="border px-2 py-1">{child.lmtd_count}</td>
                      <td className="border px-2 py-1">{child.lmtd_sal}</td>
                      <td className="border px-2 py-1">{child.mtd_count}</td>
                      <td className="border px-2 py-1">{child.mtd_sal}</td>
                      <td className="border px-2 py-1">{child.change}</td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NestedColumnTable;
